import { useTranslation } from "next-i18next";
import { ScrollView } from "../UI/ScrollView";
import Image from "next/legacy/image";
import { formatThousand } from "utils";
import UncheckedIcon from "../../public/icon/unchecked2.svg";
import CheckedIcon from "../../public/icon/checked2.svg";
import Button from "../UI/Button";
import classnames from "classnames";
import { ContractInfo } from "./contract-info";
import { Confirmed } from "./confirmed";
import { translate } from "utils/i18n";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { goodExchangeApi, goodsPostageApi } from "utils/api";
import { BootstrapInput, BootstrapTextarea } from "../UI/BootstrapInput";
import { ErrorTips } from "../UI/ErrorTips";
import BackIcon from "../../public/img/mall/back.svg";
import { useRouter } from "next/router";
import { Checkbox } from "./checkbox";
import { Select } from "../UI/select";
import { LottiePlayer } from "../common/lottie-player";
import { ItemBox } from "./item-box";
import { debounce } from "@mui/material";

const areaOptions = [
  {
    labelCn: "中国大陆",
    labelEn: "Mainland China",
  },
  {
    labelCn: "港澳台",
    labelEn: "Hong Kong, Macao and Taiwan",
  },
  {
    labelCn: "海外其他地区",
    labelEn: "Other overseas regions",
  },
];

const Specification = ({ item, formValues, formErrors, onFieldChange }) => {
  const { t } = useTranslation("mall");

  return (
    <div>
      <div className="text-[22px] leading-[32px] font-bold font-DIN_Alternate mb-[24px]">
        {t("goodsSpecTitle")}
      </div>

      <div className="font-bold my-[24px] flex items-center">
        {item.specTitle}

        <span className="text-[#FF2800] ml-[4px]">*</span>

        {formErrors?.spec && (
          <ErrorTips className="ml-[8px]">{formErrors?.spec}</ErrorTips>
        )}
      </div>

      <div className="flex flex-wrap">
        {item.specList.map((spec, i) => {
          return (
            <Checkbox
              key={i}
              value={spec}
              checked={formValues?.spec === spec}
              onClick={() => {
                onFieldChange("spec", spec);
              }}
            >
              {spec}
            </Checkbox>
          );
        })}
      </div>
    </div>
  );
};

const DeliveryInfo = ({
  item,
  formValues,
  formErrors,
  onFieldChange,
  fetchGoodsPostage,
}) => {
  const { t } = useTranslation("mall");
  const { locale } = useRouter();

  useEffect(() => {
    if (formValues?.area) {
      fetchGoodsPostage({
        area: formValues.area,
        goodsId: item.goodsId,
      });
    }
  }, [formValues?.area]);

  return (
    <div>
      <div className="text-[22px] leading-[32px] font-bold font-DIN_Alternate mb-[24px]">
        {t("deliveryAddressTitle")}
      </div>

      <div className="mb-[24px]">
        <div className="font-bold mt-[26px] mb-[16px] flex items-center">
          <div>
            {t("areaTitle")}
            <span className="text-[#FF2800] ml-[4px]">*</span>
          </div>

          {formErrors?.area && (
            <ErrorTips className="ml-[8px]">{formErrors?.area}</ErrorTips>
          )}
        </div>
        <div>
          <Select
            placeholder={t("areaPlaceholder")}
            maxWidth={300}
            value={formValues?.area}
            options={areaOptions.map((areaOption) => ({
              label: locale === "cn" ? areaOption.labelCn : areaOption.labelEn,
              value: areaOption.labelEn,
            }))}
            onChange={(event) => {
              onFieldChange("area", event.target.value);
            }}
            disabled
          />
        </div>
      </div>

      <div className="mb-[24px]">
        <div className="font-bold mt-[26px] mb-[16px] flex items-center">
          <div>
            {t("receiverAddressTitle")}
            <span className="text-[#FF2800] ml-[4px]">*</span>
          </div>

          {formErrors?.receiverAddress && (
            <ErrorTips className="ml-[8px]">
              {formErrors?.receiverAddress}
            </ErrorTips>
          )}
        </div>

        <div>
          <BootstrapTextarea
            className="w-full"
            style={{ minWidth: 0, minHeight: 100 }}
            placeholder={t("receiverAddressPlaceholder")}
            value={formValues?.receiverAddress}
            onChange={(event) => {
              onFieldChange("receiverAddress", event.target.value);
            }}
          />
        </div>

        <div className="text-[#FF2800] text-[14px] mt-[4px]">
          {t("goodsAreaTips")}
        </div>
      </div>

      <div className="mb-[24px]">
        <div className="font-bold mt-[26px] mb-[16px] flex items-center">
          <div>
            {t("receiverTitle")}
            <span className="text-[#FF2800] ml-[4px]">*</span>
          </div>

          {formErrors?.receiver && (
            <ErrorTips className="ml-[8px]">{formErrors?.receiver}</ErrorTips>
          )}
        </div>

        <div>
          <BootstrapInput
            className="w-full"
            placeholder={t("receiverPlaceholder")}
            value={formValues?.receiver}
            onChange={(event) => {
              onFieldChange("receiver", event.target.value);
            }}
          />
        </div>
      </div>

      <div className="mb-[24px]">
        <div className="font-bold mt-[26px] mb-[16px] flex items-center">
          <div>
            {t("receiverPhoneTitle")}
            <span className="text-[#FF2800] ml-[4px]">*</span>
          </div>

          {formErrors?.receiverPhone && (
            <ErrorTips className="ml-[8px]">
              {formErrors?.receiverPhone}
            </ErrorTips>
          )}
        </div>

        <div>
          <BootstrapInput
            className="w-full"
            placeholder={t("receiverPhonePlaceholder")}
            value={formValues?.receiverPhone}
            onChange={(event) => {
              onFieldChange("receiverPhone", event.target.value);
            }}
          />
        </div>
      </div>

      <div className="mb-[24px]">
        <div className="font-bold mt-[26px] mb-[16px] flex items-center">
          <div>{t("postalCodeTitle")}</div>
        </div>

        <div>
          <BootstrapInput
            className="w-full"
            value={formValues?.postalCode}
            onChange={(event) => {
              onFieldChange("postalCode", event.target.value);
            }}
          />
        </div>
      </div>
    </div>
  );
};

const Confirm = ({
  item,
  formValues,
  formErrors,
  onFieldChange,
  shouldSave,
  setShouldSave,
  onConfirmRedeem,
  validateField,
  title,
  goodsPostageData,
  fetchGoodsPostage,
  windowHeight,
}) => {
  const { t } = useTranslation("mall");
  const startStep = item.specList?.length > 0 ? 1 : 2;
  const [step, setStep] = useState(startStep);

  const handleNext = () => {
    if (item.specList?.length > 0 && !validateField("spec", formValues?.spec)) {
      return;
    }

    if (step === 2 && !validateField("email", formValues?.email)) {
      return;
    }

    setStep(step + 1);
  };

  return (
    <div className="bg-white pb-[40px]">
      <div className="h-[80px] flex items-center justify-center font-DIN_Alternate font-bold text-[24px] border-0 border-b border-solid border-black relative">
        {t("orderConfirmTitle")}

        {step > startStep && (
          <button
            className="absolute top-1/2 left-[24px] -translate-y-1/2 h-[32px] px-[16px] btn-unstyled rounded-full flex justify-center items-center align-middle text-[16px] transition border border-black border-solid hover:text-[#ff2800] hover:border-[#ff2800] font-medium"
            onClick={() => {
              setStep(step - 1);
            }}
          >
            <BackIcon />
            <span className="ml-[8px]">{t("backBtn")}</span>
          </button>
        )}
      </div>

      <div className="w-[400px] mx-auto mt-[24px]">
        <ItemBox
          img={item.mainPic}
          title={title}
          postage={goodsPostageData?.extraPoints}
          cupPoints={item.cupPoints}
        />
        <div className="h-[1px] bg-[#E3E5E7] scale-y-50 my-[24px]"></div>

        <div className="relative">
          <ScrollView
            className="overflow-auto pr-[24px] w-[424px] pb-[24px]"
            style={{ maxHeight: Math.min(442, windowHeight - 400) }}
          >
            <div>
              {step === 1 && (
                <Specification
                  item={item}
                  formValues={formValues}
                  formErrors={formErrors}
                  onFieldChange={onFieldChange}
                />
              )}

              {step === 2 && (
                <ContractInfo
                  formValues={formValues}
                  formErrors={formErrors}
                  onFieldChange={onFieldChange}
                />
              )}

              {step === 3 && (
                <>
                  <DeliveryInfo
                    item={item}
                    formValues={formValues}
                    formErrors={formErrors}
                    onFieldChange={onFieldChange}
                    fetchGoodsPostage={fetchGoodsPostage}
                  />

                  <div className="flex items-center">
                    <button
                      className="btn-unstyled h-[16px]"
                      onClick={() => {
                        setShouldSave(!shouldSave);
                      }}
                    >
                      {shouldSave ? (
                        <CheckedIcon className="w-[16px]" />
                      ) : (
                        <UncheckedIcon className="w-[16px]" />
                      )}
                    </button>
                    <span className="ml-[8px] text-[14px] font-medium transition">
                      {t("saveTips")}
                    </span>
                  </div>
                </>
              )}
            </div>
          </ScrollView>
          <div className="absolute bottom-[-2px] w-full h-[24px] bg-gradient-to-t from-white to-[rgba(255,255,255,0.0001)]"></div>
        </div>

        <div className="mt-[24px]">
          {step < 3 && (
            <Button
              variant="contained"
              color="secondary"
              component="span"
              className={classnames(
                "w-full h-full rounded-full transition font-black text-[18px]",
                "text-white hover:opacity-80"
              )}
              onClick={handleNext}
            >
              <span>{t("nextStepBtn")}</span>
            </Button>
          )}

          {step === 3 && (
            <Button
              variant="contained"
              color="secondary"
              component="span"
              className={classnames(
                "w-full h-full rounded-full transition font-black text-[18px]",
                "text-white hover:opacity-80"
              )}
              onClick={debounce(onConfirmRedeem, 300)}
            >
              <span>{t("btnConfirm")}</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const Success = ({ item, goodExchangeData, title, windowHeight }) => {
  const { t } = useTranslation("mall");

  return (
    <ScrollView
      className="overflow-auto relative"
      style={{ maxHeight: windowHeight - 32 }}
    >
      <LottiePlayer />
      <Confirmed />
    </ScrollView>
  );
};

export const ItemModalOrderGoods = ({
  item,
  formValues,
  formErrors,
  onFieldChange,
  shouldSave,
  setShouldSave,
  validateField,
  title,
  windowHeight,
}) => {
  const [step, setStep] = useState(1);
  const [goodExchangeData, setGoodExchangeData] = useState();
  const [goodsPostageData, setGoodsPostageData] = useState();
  const { enqueueSnackbar } = useSnackbar();

  const fetchGoodsPostage = async (data) => {
    const goodsPostageApiRes = await goodsPostageApi(data);
    console.log("goodsPostageApiRes", { goodsPostageApiRes });
    setGoodsPostageData(goodsPostageApiRes.data);
  };

  const handleConfirmRedeem = async () => {
    if (item.specList?.length > 0 && !validateField("spec", formValues?.spec)) {
      return;
    }

    if (!validateField("area", formValues?.area)) {
      return;
    }

    if (!validateField("receiverAddress", formValues?.receiverAddress)) {
      return;
    }

    if (!validateField("receiver", formValues?.receiver)) {
      return;
    }

    if (!validateField("receiverPhone", formValues?.receiverPhone)) {
      return;
    }

    const goodExchangeApiRes = await goodExchangeApi({
      goodsId: item.goodsId,
      contact: {
        emailAddress: formValues.email,
        twitterHandle: formValues.twitterHandle,
        phoneNumber: formValues.phone,
      },
      postAddress: {
        address: formValues.receiverAddress,
        receiverName: formValues.receiver,
        phoneNumber: formValues.receiverPhone,
        postalCode: formValues.postalCode,
      },
      discordId: formValues.discordId,
      area: formValues.area,
      spec: formValues.spec,
      isRemembered: shouldSave,
    });
    console.log("goodExchangeApiRes", { goodExchangeApiRes });

    if (goodExchangeApiRes.err) {
      enqueueSnackbar(goodExchangeApiRes?.err?.response?.data?.message, {
        variant: "error",
      });
      return;
    }

    setGoodExchangeData(goodExchangeApiRes.data);

    setStep(2);
  };

  console.log("ItemModalOrderGoods", { item });

  return (
    <>
      {step === 1 && (
        <Confirm
          item={item}
          formValues={formValues}
          formErrors={formErrors}
          onFieldChange={onFieldChange}
          shouldSave={shouldSave}
          setShouldSave={setShouldSave}
          onConfirmRedeem={handleConfirmRedeem}
          validateField={validateField}
          title={title}
          goodsPostageData={goodsPostageData}
          fetchGoodsPostage={fetchGoodsPostage}
          windowHeight={windowHeight}
        />
      )}

      {step === 2 && (
        <Success
          item={item}
          goodExchangeData={goodExchangeData}
          title={title}
          windowHeight={windowHeight}
        />
      )}
    </>
  );
};
